// AccionesPac.tsx
import React, { useMemo, useState } from "react"; // Agregamos useMemo
import ActionMenu, { Action } from "../../../../components/ui/materialui/actionMenu";
import ConfirmationModal from "../../../../components/ui/materialui/confirmationModal";
import { useToast } from "../../../../hooks/useToast";
import { AdministrarPacEdit, RespuestaPacDTO } from "../models/administrarPac";
import EditIcon from "@mui/icons-material/Edit";
import { GridDeleteIcon } from "@mui/x-data-grid";
import AddCircleOutlineTwoToneIcon from '@mui/icons-material/AddCircleOutlineTwoTone';
import { eliminarPac } from "../service/servicePac";
import { TipoFuncionalidad } from "../../../../utils/enums"; // Importamos TipoFuncionalidad
import { usePermissions } from "../../../../components/auth/ProtectedRoute"; // Importamos hook de permisos

interface IProps {
  pac: RespuestaPacDTO;
  control: any;
  setPacEditar: React.Dispatch<React.SetStateAction<AdministrarPacEdit | undefined>>;
  setModalDetalle: React.Dispatch<React.SetStateAction<boolean>>;
  isEdit: (value: boolean) => void;
}

const AccionesPac: React.FC<IProps> = ({ pac, control, setPacEditar, setModalDetalle, isEdit }) => {
  const { addToast } = useToast();
  const { hasPermission } = usePermissions(); 
  
  const [modalConfirmacion, setModalConfirmacion] = useState({
    show: false,
    title: '',
    message: '',
    metodo: () => { },
  });

  const isValueEmpty = (value: any): boolean => {
    if (value === null || value === undefined) return true;
    if (typeof value === 'string' && value.trim() === '') return true;
    if (typeof value === 'number' && value === 0) return true;
    return false;
  };

  const isCreateMode = isValueEmpty(pac.llavePac) || isValueEmpty(pac.estado);

  const handleEditarClick = () => {
    const pacParaEditar: AdministrarPacEdit = {
      mutuoId: pac.id || 0,
      llavePac: String(pac.llavePac),
      bancoCodigo: '',
      fechaPrimerDescuento: new Date(),
      tipoCuota: '',
      diaCobro: 0,
      numeroMandato: 0,
      numeroOperacion: pac.numeroOperacion,
      cuentaCorriente: '',
      estado: pac.estado,
      montoCuota: pac.montoCuota || 0,
      tipoMoneda: pac.codigoMoneda,
      observaciones: '',
      tipoProducto: pac.tipoProducto
    };
    setPacEditar({ ...pacParaEditar });
    setModalDetalle(true);
    isEdit(true);
  };

  const handleCrearClick = () => {
    const pacParaCrear: AdministrarPacEdit = {
      mutuoId: pac.id ?? 0,
      llavePac: pac.llavePac,
      bancoCodigo: '',
      fechaPrimerDescuento: new Date(),
      tipoCuota: '',
      diaCobro: 0,
      numeroMandato: 0,
      estado: '',
      montoCuota: pac.montoCuota || 0,
      observaciones: '',
      tipoMoneda: pac.codigoMoneda,
      numeroOperacion: pac.numeroOperacion,
      cuentaCorriente: '',
      tipoProducto: pac.tipoProducto,
      numeroSolicitud: pac.numeroSolicitud?.toString() || ''
    };
    setPacEditar(pacParaCrear);
    setModalDetalle(true);
    isEdit(false);
  };

  const handleEliminarClick = () => {
    setModalConfirmacion({
      show: true,
      title: 'Confirmar Eliminación',
      message: `¿Está seguro de eliminar el PAC para el mutuo seleccionado?`,
      metodo: () => confirmarEliminar(),
    });
  };

  const confirmarEliminar = async () => {
    try {
      const eliminado = await eliminarPac(pac.id || 0);
      if (eliminado) {
        addToast({
          mostrar: true,
          titulo: 'Éxito',
          tipo: 'Success',
          mensaje: 'Acción completada con éxito. Recuerde regenerar el dividendo vigente.'
        });
        control.fetchData();
      }
    } catch (error) {
      addToast({
        mostrar: true,
        titulo: 'Error',
        tipo: 'Danger',
        mensaje: 'Error al eliminar el PAC'
      });
    } finally {
      setModalConfirmacion(prev => ({ ...prev, show: false }));
    }
  };

  const actions = useMemo<Action[]>(() => {
    const items: Action[] = [];
    
    if (!hasPermission(TipoFuncionalidad.Recaudacion_adm)) {
      return items;
    }

    if (isCreateMode) {
      items.push({ 
        label: "Crear", 
        icon: <AddCircleOutlineTwoToneIcon fontSize="small" />, 
        onClick: handleCrearClick 
      });
    } else {
      items.push(
        { 
          label: "Editar", 
          icon: <EditIcon fontSize="small" />, 
          onClick: handleEditarClick 
        },
        { 
          label: "Eliminar", 
          icon: <GridDeleteIcon fontSize="small" />, 
          onClick: handleEliminarClick 
        }
      );
    }

    return items;
  }, [hasPermission, isCreateMode, pac]);

  return (
    <>
      <ConfirmationModal
        onHide={() => setModalConfirmacion(prev => ({ ...prev, show: false }))}
        onConfirm={modalConfirmacion.metodo}
        show={modalConfirmacion.show}
        title={modalConfirmacion.title}
        message={modalConfirmacion.message}
      />
      {actions.length > 0 && <ActionMenu actions={actions} />}
    </>
  );
};

export default AccionesPac;